import { plural, t } from "@lingui/macro";

import { constructGridFieldObj, getChildColumns } from "./flow.util.js";
import { FieldURLMapping, PublicFieldURLMapping } from "./url.js";
import { FIELD_TYPES, APPEARANCE_TYPES } from "../../constants/index.js";
import { parseDate, dateFromString } from "../../libs/utils/date.js";
import { getTextFromMarkdown } from "../../libs/utils/markdown.js";
import {
  operatorMapping,
  isBlank,
  required,
  isEmail,
  isUnique
} from "../validations/validations.js";
import { getStyleForColor, isObject } from "../../libs/utils/utils.js";
import { getStore } from "@base/common/libs/stores/base.js";

const shouldValidate = (columnConfig) => {
  return (
    !columnConfig["isReadOnly"] || columnConfig["isExpression"]
    // columnConfig["renderedOnUI"] &&
    // columnConfig["permission"] &&
    // columnConfig["permission"] !== "Hidden"
  );
};

const validateField = (
  columnId,
  formData = {},
  modelConfig = {},
  visibility = {},
  skipDependentFields
) => {
  let error = [];
  let validationList =
    modelConfig[columnId] && modelConfig[columnId].validations;
  let renderedOnUI = visibility["_root"] && visibility["_root"][columnId];
  validationList &&
    renderedOnUI &&
    validationList.forEach((validation) => {
      let result = validation(formData);
      if (result) {
        error = [...error, ...result];
      }
    });
  if (!skipDependentFields) {
    let dependentFields =
      modelConfig[columnId] && modelConfig[columnId].dependentFields;
    dependentFields &&
      dependentFields.forEach((field) => {
        let columnId = field.columnId;
        if (shouldValidate(modelConfig[columnId])) {
          validateField(columnId, formData, modelConfig, visibility, true);
        }
      });
  }
  return { fieldId: modelConfig[columnId]["id"], error: error };
};

const validateForm = (formData, modelConfig, rootId, visibility) => {
  let formError = {};
  for (let modelName in modelConfig) {
    if (modelName === rootId) {
      let model = modelConfig[modelName];
      for (let column in model) {
        let columnConfig = model[column];
        if (shouldValidate(columnConfig)) {
          let errorObj = validateField(
            column,
            formData,
            modelConfig[modelName],
            visibility
          );
          formError = updateFieldError(
            errorObj.fieldId,
            errorObj.error,
            formError
          );
        }
      }
    } else {
      formError = validateGrid(
        formData,
        modelName,
        modelConfig,
        formError,
        visibility
      );
    }
  }
  return formError;
};

const isReadOnly = (field) => {
  return (
    (field && field["Field::Expression"]) || field.Widget === "Aggregation"
  );
};

const getFormColumns = (
  model,
  accessChildModels,
  systemFields,
  { currentStep, isPublicForm }
) => {
  let fieldList = model.getList("Model", "Field");
  let childModels = model.getList("Model", "Model");
  let modelConfiguration = {};
  modelConfiguration[model.getValue("Id")] = {};
  let parentModelConfiguration = modelConfiguration[model.getValue("Id")];
  if (childModels && childModels.length > 0 && accessChildModels) {
    childModels.forEach((childModel) => {
      let childRow = childModel.getList("Model", "Row");
      let columnList = childRow[0] && childRow[0].getList("Row", "Column");
      let childColumnIdList = columnList.map((column) => {
        let field = column.getList("Column", "Field")[0];
        return field && field["currentJson"];
      });
      let childModelConfig = constructGridFieldObj(model, childColumnIdList);
      modelConfiguration = {
        ...modelConfiguration,
        [childModel.getValue("Id")]: childModelConfig
      };
      if (!modelConfiguration[model.getValue("Id")][model.getValue("Column")]) {
        let modelColumn = model.getObject(model.getValue("Column"));
        modelConfiguration[model.getValue("Id")][model.getValue("Column")] = {
          type: modelColumn.getValue("Type"),
          id: childModel.getValue("Id")
        };
      }
    });
  }
  fieldList.forEach((field) => {
    let column = model.getObject(field.getValue("Column"));
    let columnId = column.getValue("Id");
    parentModelConfiguration[columnId] = {
      ...parentModelConfiguration[columnId],
      name: field.getValue("Name"),
      id: field.getValue("Id"),
      columnId: field.getValue("Column"),
      type: column.getValue("Type")
    };
    let parentRow = model.getObject(column.getValue("Row"));
    let parentColumn = model.getObject(parentRow.getValue("Column"));
    let [validationGeneratorResult, validationDependentFields] =
      validationGenerator(field, [], systemFields);
    // eslint-disable-next-line no-unused-vars
    let [appearanceResult, appearanceDependentFields] = appearanceGenerator(
      column,
      [],
      currentStep,
      systemFields
    );
    let visibilityGeneratorResult = visibilityGenerator(column, systemFields);
    let visibilitySectionGeneratorResult = visibilityGenerator(
      parentColumn,
      systemFields
    );
    if (
      visibilitySectionGeneratorResult &&
      !visibilitySectionGeneratorResult.isEmpty()
    ) {
      if (!parentModelConfiguration[parentColumn.getValue("Id")]) {
        parentModelConfiguration[parentColumn.getValue("Id")] = {
          name: parentColumn.getValue("Name"),
          id: parentColumn.getValue("Id"),
          type: parentColumn.getValue("Type")
        };
      }
      parentModelConfiguration[parentColumn.getValue("Id")]["visibility"] =
        visibilitySectionGeneratorResult;
    }
    if (validationGeneratorResult && validationGeneratorResult.length > 0) {
      parentModelConfiguration[columnId]["validations"] =
        validationGeneratorResult;
      if (
        parentModelConfiguration[columnId]["dependentFields"] &&
        parentModelConfiguration[columnId]["dependentFields"].length > 0
      ) {
        parentModelConfiguration[columnId]["dependentFields"] = [
          ...parentModelConfiguration[columnId]["dependentFields"],
          ...validationDependentFields
        ];
      } else {
        parentModelConfiguration[columnId]["dependentFields"] =
          validationDependentFields;
      }
    }
    if (visibilityGeneratorResult && visibilityGeneratorResult.length > 0) {
      parentModelConfiguration[columnId]["visibility"] =
        visibilityGeneratorResult;
    }
    // parentModelConfiguration[columnId]["parentVisibility"] = visibilitySectionGeneratorResult;
    parentModelConfiguration[columnId]["appearance"] = appearanceResult;
    parentModelConfiguration[columnId]["sourceUrl"] = getSourceUrl(
      model,
      column,
      isPublicForm
    );
    parentModelConfiguration[columnId]["isReadOnly"] = isReadOnly(
      field.currentJson
    );
    parentModelConfiguration[columnId]["isExpression"] =
      parentModelConfiguration[columnId]["isReadOnly"];
    if (validationDependentFields.length > 0) {
      validationDependentFields.forEach((dependentField) => {
        let dependentFieldObject = model.getObject(dependentField.fieldId);
        let dependentFieldColumn = model.getObject(dependentField.columnId);
        if (!parentModelConfiguration[dependentFieldColumn.getValue("Id")]) {
          parentModelConfiguration[dependentFieldColumn.getValue("Id")] = {
            name: dependentFieldObject.getValue("Name"),
            id: dependentFieldObject.getValue("Id"),
            columnId: dependentFieldObject.getValue("Column"),
            dependentFields: []
          };
        }
        if (
          !parentModelConfiguration[dependentFieldColumn.getValue("Id")][
            "dependentFields"
          ]
        ) {
          parentModelConfiguration[dependentFieldColumn.getValue("Id")][
            "dependentFields"
          ] = [];
        }
        parentModelConfiguration[dependentFieldColumn.getValue("Id")][
          "dependentFields"
        ] = [
          ...parentModelConfiguration[dependentFieldColumn.getValue("Id")][
            "dependentFields"
          ],
          {
            fieldId: field.getValue("Id"),
            columnId
          }
        ];
      });
    }
  });
  return modelConfiguration;
};

const getPermissionGeneratorList = (
  model,
  column,
  config,
  modelConfiguration,
  modelId,
  columnObject
) => {
  let parentRow = model.getObject(columnObject.getValue("Row"));
  let parentColumn = parentRow && model.getObject(parentRow.getValue("Column"));
  let parentPermission;
  if (!modelConfiguration[modelId]) {
    modelConfiguration[modelId] = {};
  }
  if (!modelConfiguration[modelId][column]) {
    modelConfiguration[modelId][column] = {};
  }
  if (parentColumn) {
    parentPermission = permissionGenerator(
      parentColumn,
      null,
      config.isStartStep,
      config.currentStep,
      config.status,
      config.isSendBackToInitiator
    );
    parentPermission = parentPermission["ColumnPermission"];
    let parentColumnId = parentColumn.getValue("Id");
    if (!modelConfiguration[modelId][parentColumnId]) {
      modelConfiguration[modelId][parentColumnId] = {
        type: parentColumn.getValue("Type")
      };
    }
    modelConfiguration[modelId][parentColumnId]["permission"] =
      parentPermission;
  }
  let permissionGeneratorResult = permissionGenerator(
    columnObject,
    parentPermission,
    config.isStartStep,
    config.currentStep,
    config.status,
    config.isSendBackToInitiator
  );
  permissionGeneratorResult =
    permissionGeneratorResult && permissionGeneratorResult["ColumnPermission"];
  modelConfiguration[modelId][column]["permission"] = permissionGeneratorResult;
  return permissionGeneratorResult;
};

const getFormConfiguration = (model, flowConfig, systemFields) => {
  let modelConfiguration = getFormColumns(
    model,
    false,
    systemFields,
    flowConfig
  ); //pass as argument
  let childModels = model.getList("Model", "Model");
  childModels.forEach((childModel) => {
    let parentModelId = childModel.getModel().getValue("Id");
    let column = model.getObject(childModel.getValue("Column"));
    let columnId = column.getValue("Id");
    // let parentPermission = permissionGenerator(
    //   column,
    //   null,
    //   flowConfig.isStartStep,
    //   flowConfig.currentStep,
    //   flowConfig.status,
    //   flowConfig.isSendBackToInitiator
    // );
    // let permissionObject = {
    //   [columnId]: parentPermission
    // };
    let childColumnList = getChildColumns(
      column,
      undefined,
      flowConfig,
      systemFields
    );
    modelConfiguration[childModel.getValue("Id")] = childColumnList;
    if (!modelConfiguration[parentModelId][columnId]) {
      modelConfiguration[parentModelId][columnId] = {
        type: column.getValue("Type"),
        id: childModel.getValue("Id")
      };
    }
    let visibilitySectionGeneratorResult = visibilityGenerator(
      column,
      systemFields
    );
    if (
      visibilitySectionGeneratorResult &&
      !visibilitySectionGeneratorResult.isEmpty()
    ) {
      modelConfiguration[parentModelId][columnId]["visibility"] =
        visibilitySectionGeneratorResult;
    }
    // modelConfiguration[parentModelId][columnId]["permission"] =
    //   parentPermission["ColumnPermission"];
    // modelConfiguration[parentModelId][columnId]["isReadOnly"] =
    //   (parentPermission["ColumnPermission"] === "ReadOnly" &&
    //     !flowConfig.isAdmin) ||
    //   modelConfiguration[parentModelId][columnId]["isReadOnly"];
  });
  // for (let modelId in modelConfiguration) {
  //   for (let column in modelConfiguration[modelId]) {
  //     let columnObject = model.getObject(column);
  //     // let permissionGeneratorResult = getPermissionGeneratorList(
  //     //   model,
  //     //   column,
  //     //   flowConfig,
  //     //   modelConfiguration,
  //     //   modelId,
  //     //   columnObject
  //     // );
  //     // modelConfiguration[modelId][column]["isReadOnly"] =
  //     //   (permissionGeneratorResult === "ReadOnly" && !flowConfig.isAdmin) ||
  //     //   modelConfiguration[modelId][column]["isReadOnly"];
  //     // let [appearanceResult] = appearanceGenerator(
  //     //   columnObject,
  //     //   [],
  //     //   flowConfig.currentStep,
  //     //   systemFields
  //     // );
  //     // modelConfiguration[modelId][column]["appearance"] = appearanceResult;
  //   }

  // }
  return modelConfiguration;
};

const validateGrid = (
  formData,
  modelId,
  modelConfig,
  formError,
  visibility,
  changeSummary
) => {
  let rows = formData.getList(modelId);
  // filter only updated rows on bulk operations like cell paste/ delete
  let updatedColumnsList = [];
  rows = changeSummary
    ? changeSummary.map((changedrow) => {
        updatedColumnsList = new Set([
          ...updatedColumnsList,
          ...Object.keys(changedrow)
        ]);
        return rows.find((row) => row.getValue("_id") === changedrow._id);
      })
    : rows;
  rows.forEach((row) => {
    if (changeSummary) {
      formError = validateUpdatedCells(
        formData,
        modelId,
        modelConfig,
        row,
        formError,
        visibility,
        updatedColumnsList
      );
    } else {
      formError = validateRow(
        formData,
        modelId,
        modelConfig,
        row,
        formError,
        visibility
      );
    }
  });
  return formError;
};

const validateUpdatedCells = (...props) => {
  let [
    formData,
    modelId,
    modelConfig,
    row,
    formError,
    visibility,
    updatedColumnsList
  ] = props;
  let model = modelConfig[modelId];
  for (let column in model) {
    let columnConfig = model[column];
    if (updatedColumnsList && updatedColumnsList.has(columnConfig.Id)) {
      let tableRows = formData.getList(modelId);
      let errorObj = validateGridCell(
        columnConfig,
        row,
        modelId,
        visibility,
        formData,
        tableRows
      );
      if (errorObj) {
        formError = updateGridFieldError(
          errorObj.modelId,
          errorObj.rowId,
          errorObj.fieldId,
          errorObj.error,
          formError
        );
      }
    }
  }
  return formError;
};

const validateRow = (
  formData,
  modelId,
  modelConfig,
  row,
  formError,
  visibility
) => {
  let model = modelConfig[modelId];
  for (let column in model) {
    let columnConfig = model[column];
    let tableRows = formData.getList(modelId);
    let errorObj = validateGridCell(
      columnConfig,
      row,
      modelId,
      visibility,
      formData,
      tableRows
    );
    if (errorObj) {
      formError = updateGridFieldError(
        errorObj.modelId,
        errorObj.rowId,
        errorObj.fieldId,
        errorObj.error,
        formError
      );
    }
  }
  return formError;
};

const isRendererOnUi = (visibility, modelId, rowId, columnId) => {
  return (
    visibility[modelId] &&
    visibility[modelId][rowId] &&
    visibility[modelId][rowId][columnId]
  );
};

const validateGridCell = (
  column,
  row,
  modelId,
  visibility,
  formData,
  tableRows
) => {
  let error = [];
  const columnId = column.Column;
  const validationList = column.Validationfun || [];
  if (checkFieldValidation(column)) {
    const visibleRows = column.hasConstraint
      ? tableRows.filter((row) => {
          return isRendererOnUi(
            visibility,
            modelId,
            row.id || row._id,
            columnId
          );
        })
      : [];
    isRendererOnUi(visibility, modelId, row.id || row._id, columnId) &&
      validationList.forEach((validation) => {
        let result = validation(row, formData, visibleRows, tableRows);
        if (result) {
          error = [...error, ...result];
        }
      });
    return {
      modelId: modelId,
      rowId: row.id,
      fieldId: column.Id,
      error: error
    };
  }
};

export {
  getFormColumns,
  validateField,
  getFormConfiguration,
  validateForm,
  validateGrid,
  validateGridCell,
  getPermissionGeneratorList
};

const getSourceUrl = (model, columnObject, isPublicForm) => {
  let mapping = FieldURLMapping;
  if (isPublicForm) {
    mapping = PublicFieldURLMapping;
  }
  let fields = columnObject.getList("Column", "Field");
  if (!fields.isEmpty()) {
    let fieldType = fields[0].getValue("Type");
    return mapping[fieldType];
  }
};

export function shouldAutoSave(field, saveChanges) {
  return (
    saveChanges ||
    field["Field::Node"] ||
    field["Field::RemoteLookup"] ||
    field["Autofill::QueryDefinition"] ||
    field["RemoteLookup::QueryDefinition"] ||
    field["Aggregate::QueryDefinition"] ||
    field["RHSField::Condition"] ||
    field["LHSOwnField::Condition"] ||
    field["ValueField::Property"]
  );
}

const EXPRESSION = "Field::Expression";
const CONSTRAINT = "Field::Constraint";
const CRITERIA = "FieldValidation::Criteria";
const REQUIRED = "Required";
const QUERY_DEFINITION = "QueryDefinition";
const AUTO_FILL = "AutoFill";

export function checkFieldValidation(field) {
  let validate = false;
  if (!field) return validate;
  if (field[EXPRESSION]) {
    //validate for expression fields
    validate = true;
  } else if (
    !field.ReadOnly &&
    (field[REQUIRED] || field[CRITERIA] || field[CONSTRAINT])
  ) {
    //validate for editable fields with validation
    validate = true;
  } else if (
    field[QUERY_DEFINITION] &&
    field[QUERY_DEFINITION].getValue(AUTO_FILL)
  ) {
    //validate for auto select user
    validate = true;
  }
  return validate;
}

export function updateGridFieldError(
  tableId,
  rowId,
  fieldId,
  error,
  formError
) {
  let errorObj = { ...formError };
  if (errorObj[tableId] === undefined) {
    errorObj[tableId] = {};
  } else {
    errorObj[tableId] = { ...errorObj[tableId] };
  }
  if (errorObj[tableId][rowId] === undefined) {
    errorObj[tableId][rowId] = {};
  } else {
    errorObj[tableId][rowId] = { ...errorObj[tableId][rowId] };
  }
  if (error && !error.isEmpty()) {
    errorObj[tableId][rowId][fieldId] = error;
  } else {
    Reflect.deleteProperty(errorObj[tableId][rowId], fieldId);
  }
  return errorObj;
}

export function clearTableRowError(tableId, rowIds, formError) {
  let errorObj = { ...formError };
  if (errorObj[tableId] === undefined) {
    errorObj[tableId] = {};
  } else {
    errorObj[tableId] = { ...errorObj[tableId] };
  }
  rowIds.forEach((rowId) => Reflect.deleteProperty(errorObj[tableId], rowId));
  return errorObj;
}

export function updateFieldError(fieldId, error, formError) {
  let errorObj = { ...formError };
  if (errorObj["_root"] === undefined) {
    errorObj["_root"] = {};
  } else {
    errorObj["_root"] = { ...errorObj["_root"] };
  }
  if (error && !error.isEmpty()) {
    errorObj["_root"][fieldId] = error;
  } else {
    Reflect.deleteProperty(errorObj["_root"], fieldId);
  }
  return errorObj;
}

export function updateGridFieldVis(
  tableId,
  rowId,
  fieldId,
  vis,
  formVisibility
) {
  let visObj = { ...formVisibility };
  if (visObj[tableId] === undefined) {
    visObj[tableId] = {};
  } else {
    visObj[tableId] = { ...visObj[tableId] };
  }
  if (visObj[tableId][rowId] === undefined) {
    visObj[tableId][rowId] = {};
  } else {
    visObj[tableId][rowId] = { ...visObj[tableId][rowId] };
  }
  visObj[tableId][rowId][fieldId] = vis;
  return visObj;
}
export function updateFieldVis(fieldId, vis, formVisibility) {
  let visObj = { ...formVisibility };
  if (visObj["_root"] === undefined) {
    visObj["_root"] = {};
  } else {
    visObj["_root"] = { ...visObj["_root"] };
  }
  visObj["_root"][fieldId] = vis;
  return visObj;
}

export function shouldTriggerSave(changeSet) {
  let shouldTrigger = false;
  if (changeSet) {
    let keys = Object.keys(changeSet);
    keys.forEach((key) => {
      if (key.indexOf("Table::") === 0) {
        if (changeSet[key].length > 0) {
          shouldTrigger = true;
        }
      } else if (key !== "_id") {
        shouldTrigger = true;
      }
    });
  }
  return shouldTrigger;
}

// Used for highlight the field, Mostly when the file uploading and error state
export async function applyFieldHighlight(storeId, fieldIds = {}) {
  try {
    const fieldIdList = Object.keys(fieldIds);
    let isToasterShown;

    for (const [index, fieldId] of fieldIdList.entries()) {
      if (isToasterShown) return;

      const isChildTable = isObject(fieldIds[fieldId]);
      if (isChildTable) {
        const { Toast } = await import("widgets/feedback");

        const columnIds = fieldIds[fieldId];
        for (const columnId in columnIds) {
          if (isToasterShown) return;

          const rowIds = fieldIds[fieldId][columnId];
          for (const row in rowIds) {
            // eslint-disable-next-line max-depth
            if (isToasterShown) return;

            const cell = fieldIds[fieldId][columnId][row];
            const uploadingCount = cell["UPLOAD"];
            const errorCount = cell["ERROR"];
            // eslint-disable-next-line max-depth
            if (uploadingCount || errorCount) {
              isToasterShown = true;
              const useFormStore = getStore(storeId);
              const { schema } = useFormStore.getState();
              const tableName = schema.dataSet[fieldId].Name;
              // eslint-disable-next-line max-depth
              if (errorCount) {
                Toast.showError({
                  message: plural(errorCount, {
                    one: `# file failed to upload in ${tableName}, please retry.`,
                    other: `# files failed to upload in ${tableName}, please retry.`
                  })
                });
              } else if (uploadingCount) {
                Toast.showWarning({
                  message: t`Please wait, some files are yet to upload in ${tableName} table.`
                });
              }
            }
          }
        }
      }
      const highlightedFieldElement = document.getElementById(fieldId);
      if (index === 0) {
        highlightedFieldElement.scrollIntoView({
          block: "center",
          behavior: "smooth"
        });
      }
      highlightedFieldElement.dataset.highlight = true;
      setTimeout(function removeFocusingClass() {
        highlightedFieldElement.dataset.highlight = false;
      }, 2000);
    }
  } catch (e) {
    console.error("not able to highlight field", e);
  }
}

//Object creation

const castValue = (value, type) => {
  if (type === FIELD_TYPES.DATE_TIME && value) {
    if (new Date(parseDate(value, true, true)) !== "Invalid Date") {
      value = parseDate(value, true, true);
    }
    return new Date(dateFromString(value));
  }
  if (type === FIELD_TYPES.DATE && value) {
    return new Date(value);
  }
  if (type === FIELD_TYPES.CURRENCY && value) {
    let iValue = value.split(" ");
    value = parseFloat(iValue.join(" "));
    return value;
  }
  if (
    value &&
    (type === FIELD_TYPES.NUMBER ||
      type === FIELD_TYPES.SLIDER ||
      type === FIELD_TYPES.STAR_RATING)
  ) {
    value = parseFloat(value);
  }
  if (type === FIELD_TYPES.TEXT_AREA) {
    return getTextFromMarkdown(value);
  }
  return value;
};

const constructCondition = (
  condition,
  dependentFields,
  addLHS,
  systemFields
) => {
  let operator = condition.getValue("Operator");
  let RHS = condition.getValue("RHS" + condition.getValue("RHSType"));
  let LHSField = condition.getValue("LHSOwnField");
  let isAppearance = false;
  if (!LHSField) {
    let criteriaId = condition.getValue("Criteria");
    if (criteriaId) {
      let criteriaModel = condition.getModel(criteriaId);
      LHSField = criteriaModel.getValue("FieldValidation");
    } else if (condition.getValue("Appearance")) {
      isAppearance = true;
      let appearanceModel = condition.getModel(
        condition.getValue("Appearance")
      );
      let columnId = appearanceModel.getValue("Column");
      let columnModel = condition.getModel(columnId);
      LHSField = columnModel.getList("Column", "Field")[0].getValue("Id");
    }
  }
  let errorMessage = condition.getValue("ErrorMessage");
  let LHS =
    LHSField &&
    condition.getModel(LHSField) &&
    condition.getModel(LHSField).getValue("Id");
  let lhsFieldType =
    LHSField &&
    condition.getModel(LHSField) &&
    condition.getModel(LHSField).getValue("Type");
  if (!LHS) {
    let matchedField;
    matchedField =
      systemFields &&
      systemFields.filter((field) => {
        return field.Id === LHSField;
      });
    if (matchedField && !matchedField.isEmpty()) {
      LHS = matchedField[0]["Id"];
      lhsFieldType = matchedField[0]["Type"];
    }
  }
  if (condition.getValue("RHSType") === "Field") {
    let rhsFieldType, RHSName;
    if (condition.getModel(RHS)) {
      rhsFieldType =
        condition.getModel(RHS) && condition.getModel(RHS).getValue("Type");
      RHSName =
        condition.getModel(RHS) && condition.getModel(RHS).getValue("Name");
      RHS = condition.getModel(RHS) && condition.getModel(RHS).getValue("Id");
      let RHSColumnId =
        condition.getModel(RHS) && condition.getModel(RHS).getValue("Column");
      dependentFields = [
        ...dependentFields,
        { fieldId: RHS, columnId: RHSColumnId }
      ];
    } else {
      let matchedField;
      matchedField =
        systemFields &&
        systemFields.filter((field) => {
          return field.Id === RHS;
        });
      if (matchedField && !matchedField.isEmpty()) {
        RHS = matchedField[0]["Id"];
        RHSName = matchedField[0]["Name"];
        rhsFieldType = matchedField[0]["Type"];
      }
    }
    return [
      (allValues, isVisibility, formData, systemFields) => {
        try {
          // need to remove and check once fixed
          let lhsValue = castValue(
            allValues && allValues.getValue(LHS),
            lhsFieldType || rhsFieldType
          );
          let rhsValue = castValue(
            allValues && allValues.getValue(RHS),
            rhsFieldType
          );
          // let matchedField;
          // matchedField =
          //   systemFields &&
          //   systemFields.filter((field) => {
          //     return field.Id === RHS;
          //   });
          // if (
          //   matchedField &&
          //   !matchedField.isEmpty() &&
          //   (!rhsValue || rhsValue === "") &&
          //   formData
          // ) {
          //   rhsValue = castValue(formData.getValue(RHS), rhsFieldType);
          // }
          let result =
            operatorMapping[operator] &&
            operatorMapping[operator](
              lhsValue,
              rhsValue,
              RHSName,
              lhsFieldType
            );
          if (
            (isAppearance || isVisibility) &&
            isBlank(lhsValue) &&
            operator !== "EMPTY" &&
            operator !== "NOT_EMPTY"
          ) {
            result = "Appearance is blank";
          }
          if (result) {
            if (addLHS) {
              result = `${
                LHSField && condition.getModel(LHSField).getValue("Name")
              }: ${result}`;
            }
            return errorMessage || result;
          }
          return undefined;
        } catch (e) {
          console.error("Couldnt evaluate condition", e);
          return undefined;
        }
      },
      dependentFields
    ];
  }
  return [
    (allValues, isVisibility) => {
      // need to remove and check once fixed
      let lhsValue = castValue(
        allValues && allValues.getValue(LHS),
        lhsFieldType
      );
      let rhsValue = castValue(RHS, lhsFieldType);
      let result =
        operatorMapping[operator] &&
        operatorMapping[operator](lhsValue, rhsValue, null, lhsFieldType);
      if (
        (isAppearance || isVisibility) &&
        isBlank(lhsValue) &&
        operator !== "EMPTY" &&
        operator !== "NOT_EMPTY"
      ) {
        return "Appearance is blank";
      }
      if (result) {
        if (addLHS) {
          result = `${
            LHSField && condition.getModel(LHSField).getValue("Name")
          }: ${result}`;
        }
        return errorMessage || result;
      }
      return undefined;
    },
    dependentFields
  ];
};

export const constructCriteria = (
  criteriaList,
  dependentFields,
  addLHS,
  combineResults,
  systemFields
) => {
  let criteriaExpressionList = criteriaList.map((criteria) => {
    let isOR = criteria.getValue("IsOR");
    let conditionExpressionList = [];
    try {
      conditionExpressionList = criteria
        .getList("Criteria", "Condition")
        .map((condition) => {
          let result = constructCondition(
            condition,
            dependentFields,
            addLHS,
            systemFields
          );
          dependentFields = result[1];
          return result[0];
        });
    } catch (e) {
      console.error("Couldn't construct condition", e);
    }

    let criteriaExpression = (allValues, formData) => {
      let conditionResult =
        conditionExpressionList &&
        conditionExpressionList.map((condition) => {
          return condition(
            allValues,
            Object.keys(criteria.currentJson).includes("ColumnVisibility"),
            formData,
            systemFields
          );
        });
      conditionResult = conditionResult.filter((result) => {
        return result !== undefined;
      });
      if (
        conditionResult &&
        conditionExpressionList &&
        conditionResult.length < conditionExpressionList.length
      ) {
        if (isOR || (!isOR && conditionResult.length === 0)) {
          if (Object.keys(criteria.currentJson).includes("ColumnVisibility")) {
            return criteria.getValue("IsHidden") ? "Hidden" : "Visible";
          }
          return undefined;
        }
      }
      return combineResults ? conditionResult : conditionResult[0];
    };

    return criteriaExpression;
  });
  return [criteriaExpressionList, dependentFields];
};

export const validationCheckKey = (field, dependentFields) => {
  let validationList = [];
  if (field["Required"]) {
    let requiredFunc = (formData) => {
      let result = required(formData.getValue(field["Id"]));
      if (result) {
        return [result];
      }
      return result;
    };
    validationList = [...validationList, requiredFunc];
  }
  return [validationList, dependentFields];
};

export const validationGenerator = (field, dependentFields, systemFields) => {
  let validationList = [];
  let validationExpression;
  if (field.getValue("Required")) {
    let requiredFunc = (formData) => {
      let result = required(
        formData.getValue(field.getValue("Id")),
        field.getValue("Type")
      );
      if (result) {
        return [result];
      }
      return result;
    };
    validationList = [...validationList, requiredFunc];
  }
  let constraint = field.getConstraint();
  if (constraint) {
    let errorMessage = constraint.getErrorMessage();
    let uniqueFunc = (formData, _row, visibleRows = []) => {
      let result = isUnique({
        formData,
        field,
        visibleRows,
        errorMessage
      });
      if (result) {
        return [result];
      }
      return result;
    };
    validationList.push(uniqueFunc);
  }
  if (field.getValue("Type") === "Email") {
    let emailVatidationFunc = (formData) => {
      let result = isEmail(formData.getValue(field.getValue("Id")));
      if (result) {
        return [result];
      }
      return result;
    };
    validationList = [...validationList, emailVatidationFunc];
  }
  if (field.getList("FieldValidation", "Criteria").length !== 0) {
    [validationExpression, dependentFields] = constructCriteria(
      field.getList("FieldValidation", "Criteria"),
      dependentFields,
      null,
      true,
      systemFields
    );
    let validationFunction = (allValues, formData) => {
      let result =
        validationExpression[0] && validationExpression[0](allValues, formData);
      if (result) {
        return result;
      }
      return undefined;
    };
    validationList = [...validationList, validationFunction];
  }

  return [validationList, dependentFields];
};

export const permissionGenerator = (
  column,
  sectionPermission,
  isStartStep,
  currentStepId,
  status,
  isSendBackToInitiator
) => {
  let permissionObject = {
    ColumnPermission: isStartStep ? "Editable" : "ReadOnly"
  };
  if (sectionPermission) {
    permissionObject["ColumnPermission"] = sectionPermission;
    if (sectionPermission === "Hidden") {
      return { ColumnPermission: "Hidden" };
    }
  }
  let columnPermission = column
    .getList("Column", "Permission")
    .filter((permission) => permission.getValue("Activity") === currentStepId);
  if (columnPermission.length > 0) {
    permissionObject["ColumnPermission"] =
      columnPermission[0].getValue("Permission");
  }
  if (
    (!isStartStep &&
      status === "Completed" &&
      permissionObject["ColumnPermission"] !== "Hidden") ||
    (isStartStep &&
      status &&
      status !== "Draft" &&
      !isSendBackToInitiator &&
      permissionObject["ColumnPermission"] === "Editable" &&
      currentStepId)
  ) {
    permissionObject["ColumnPermission"] = "ReadOnly";
  }
  return permissionObject;
};

/**
 *
 * Returns an array of appearance conditions and dependent fields.
 *
 * Prioritizes conditions in the order: Default, Permission (Activity), Conditions.
 * Default and Permission appearances have lower priority compared to Conditional appearances.
 *
 * Note: Also we are converting the old color value of Default, Conditions, Activity steps to the
 *       new Style metadata for fallback support.
 *
 * @param {object} column - The column that is processed for the appearance list
 * @param {array} dependentFields - List of dependent fields for the current column
 * @param {string} currentStep - The Current Activity Id
 * @param {array} systemFields - List of system fields
 * @returns {array} An Array of array contains the list of appearance conditions and dependentFields
 */

export const appearanceGenerator = (
  column,
  dependentFields,
  currentStep,
  systemFields
) => {
  let appearanceCondition = [];
  let defaultStyle = getStyleForColor(column.getColor());
  if (defaultStyle) {
    appearanceCondition.push({
      style: defaultStyle,
      type: APPEARANCE_TYPES.DEFAULT,
      condition: () => {
        return false;
      }
    });
  }
  let activityAppearances = column.getList("ActivityColumn", "Appearance");
  let activityAppearance = activityAppearances.filter((appearance) => {
    return appearance.getValue("Activity") === currentStep;
  });
  let activityStyle;
  if (activityAppearance[0] && activityAppearance[0].getColor()) {
    activityStyle = getStyleForColor(activityAppearance[0].getColor());
  } else if (activityAppearance[0] && activityAppearance[0].getStyleModel()) {
    activityStyle = activityAppearance[0].getStyleModel().getStyle();
  }
  if (activityStyle) {
    appearanceCondition.push({
      style: activityStyle,
      type: APPEARANCE_TYPES.ACTIVITY,
      condition: () => {
        return false;
      }
    });
  }
  let appearanceList = column.getAllAppearance();
  appearanceList.forEach((appearance) => {
    let condition = appearance.getCondition();
    let color = appearance.getColor();
    let style = color
      ? getStyleForColor(color)
      : appearance.getStyleModel()?.getStyle() || null;

    if ((style && Object.keys(style).length) || condition) {
      let conditionFunc = () => {
        return false;
      };
      let type = APPEARANCE_TYPES.DEFAULT;
      if (condition) {
        let result = constructCondition(
          condition,
          dependentFields,
          systemFields
        );
        dependentFields = result[1];
        conditionFunc = result[0];
        type = APPEARANCE_TYPES.CONDITIONAL;
        appearanceCondition.push({
          style,
          type,
          condition: conditionFunc
        });
      } else {
        // If default style, it has least priority than Acitivy and Condition, so pushing to first index
        appearanceCondition.unshift({
          style,
          type,
          condition: conditionFunc
        });
      }
    }
  });

  return [appearanceCondition, dependentFields];
};

export const visibilityGenerator = (column, systemFields) => {
  if (!column) {
    return;
  }
  let visibilityCriterias = constructCriteria(
    column.getList("ColumnVisibility", "Criteria"),
    [],
    null,
    false,
    systemFields
  );
  return visibilityCriterias[0];
};
